import AddBoxIcon from "@mui/icons-material/AddBox";
import AddModeratorIcon from "@mui/icons-material/AddModerator";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PostAddIcon from '@mui/icons-material/PostAdd';
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import { Theme, useTheme } from "@mui/material";
import Alert, { AlertProps } from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Portal from '@mui/material/Portal';
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDialog } from "app/providers/dialog";
import { RootState, useAttorneyHubDispatch } from "app/realtime-store/redux-store";
import axios, { CanceledError } from "axios";
import {
  enumKeyOf,
  enumParse
} from "common/helpers/utils";
import AHDate from "common/values/date/date";
import Guid from "common/values/guid/guid";
import Document from "documents/entities/document/document";
import {
  DocumentError,
  getAllDocumentErrors,
  getAreErrorsLoadingDocumentsByIds,
  getDocumentsByIds,
  getLoadingDocumentIds
} from "documents/entities/document/store/documents-redux-slice";
import UnavailableDocument from "documents/values/unavailable-document";
import EntityAgreement from "legal-entities/entities/entity-agreement/entity-agreement";
import {
  EntityAgreementError,
  getAllEntityAgreementErrors,
  getAreErrorsLoadingEntityAgreementsByIds,
  getEntityAgreementsByIds,
  getLoadingEntityAgreementIds
} from "legal-entities/entities/entity-agreement/store/entity-agreements-redux-slice";
import EntityMemberAPIService from "legal-entities/entities/entity-member/api/entity-member-api-service";
import EntityMember from "legal-entities/entities/entity-member/entity-member";
import LegalEntity from "legal-entities/entities/legal-entity/legal-entity";
import Individual from "marketplace/entities/individual/individual";
import {
  getAllIndividualErrors,
  getAreErrorsLoadingIndividualsByUserIds,
  getIndividualsByUserIds,
  getLoadingIndividualUserIds,
  IndividualError
} from "marketplace/entities/individual/realtime-store/individuals-redux-slice";
import {
  type MRT_Cell as Cell,
  type MRT_ColumnDef as ColumnDef,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_Row as Row,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable
} from "material-react-table";
import Forum from "messaging/entities/forum/forum";
import {
  ForumError,
  getAllForumErrors,
  getAreErrorsLoadingForumsByIds,
  getForumsByIds,
  getLoadingForumIds
} from "messaging/entities/forum/store/forums-redux-slice";
import PermissionsAPIService, { PermissionsByGranteeId } from "permissions/entities/permission/api/permissions-api-service";
import Permission from "permissions/entities/permission/permission";
import PermissionTemplate from "permissions/entities/permission/permission-template";
import {
  AccessLevel,
  Resource,
  ResourceConstructor,
  Scope
} from "permissions/entities/permission/resources/general";
import {
  EntityAgreementAccess,
  LegalEntityAccess,
  LegalEntityAccessType
} from 'permissions/entities/permission/resources/legal-entities-resources';
import {
  addPermission,
  addPermissions,
  disablePermission,
  enablePermission,
  getErrorLoadingPermissionsByGranteeId,
  getIsLoadingPermissionsByGranteeId,
  populatePermissionsByGranteeId
} from "permissions/entities/permission/store/permissions-redux-slice";
import PermissionsFilterDrawer from "permissions/entities/permission/view/components/permissions-filter-drawer";
import ApplyTemplateMenuButton from "permissions/entities/permission/view/components/permissions-table/components/apply-template-menu-button";
import DocumentSubjectMatterGroupCell from "permissions/entities/permission/view/components/permissions-table/components/document-subject-matter-group-cell";
import GranteeNameCell from "permissions/entities/permission/view/components/permissions-table/components/grantee-name-cell";
import LegalEntityGroupCell from "permissions/entities/permission/view/components/permissions-table/components/legal-entity-group-cell";
import ManageTemplateRowActions from "permissions/entities/permission/view/components/permissions-table/components/manage-template-row-actions";
import PermissionCard from "permissions/entities/permission/view/components/permissions-table/components/permission-card";
import PermissionsToolbar from "permissions/entities/permission/view/components/permissions-table/components/permissions-toolbar";
import ProposalGroupCell from "permissions/entities/permission/view/components/permissions-table/components/proposal-group-cell";
import SingleGranteeResourceCell from "permissions/entities/permission/view/components/permissions-table/components/single-grantee-resource-cell";
import WorkAgreementGroupCell from "permissions/entities/permission/view/components/permissions-table/components/work-agreement-group-cell";
import { useAutocompleteFilterProps } from "permissions/entities/permission/view/components/permissions-table/hooks/use-autocomplete-filter-props";
import usePermissionsQuerySync from "permissions/entities/permission/view/components/permissions-table/hooks/use-permissions-query-sync";
import usePopulateRelatedEntities from "permissions/entities/permission/view/components/permissions-table/hooks/use-populate-related-entities";
import {
  AlertBarContainer,
  AlertBarSelectionContainer,
  MobileButton,
  NoRowsText,
  ToolbarButtons,
  ToolbarButtonsContainer
} from "permissions/entities/permission/view/components/permissions-table/styles";
import {
  AccessTypeEnumByResourceType,
  CardType,
  CategoryLabel,
  ControlMeta,
  GroupedCellConfig,
  MetaControlType,
  PermissionProps,
  SortedColumnDef,
  TableRow,
  TopLevelTemplatesIndex,
  UniversalCellConfig
} from "permissions/entities/permission/view/components/permissions-table/types";
import {
  childResourceConstructors,
  clampByCategory,
  classNameFromResource,
  clearPermissionFilters,
  computeChildVisibility,
  computeTopLevelVisibility,
  getDocumentName,
  getEntityAgreementName,
  getForumName,
  getLegalEntityName,
  getProposalName,
  getWorkAgreementName,
  isEqualVisibility,
  isPermissionsRow,
  isTemplateRow,
  isTopLevelParent,
  sumPermissionCounts
} from "permissions/entities/permission/view/components/permissions-table/utils";
import {
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useSession } from "users/session/session-context";
import UserEntityInfo from "users/values/user-entity-info/user-entity-info";
import BarebonesProposalInfoAPIResponse from "work/entities/proposal/api/response-contracts/barebones-proposal-info-api-response";
import {
  getAllProposalErrors,
  getAreErrorsLoadingProposalsByIds,
  getLoadingProposalIds,
  getProposalsByIds,
  ProposalError
} from "work/entities/proposal/store/proposals-redux-slice";
import BarebonesWorkAgreementInfoAPIResponse from "work/entities/work-agreement/api/request-contracts/barebones-work-agreement-api-response";
import {
  getAllWorkAgreementErrors,
  getAreErrorsLoadingWorkAgreementsByIds,
  getLoadingWorkAgreementIds,
  getWorkAgreementsByIds,
  WorkAgreementError
} from "work/entities/work-agreement/store/work-agreement-redux-slice";
import {
  DocumentAccess,
  DocumentContentsAccess,
  DocumentSubjectMatter
} from "../../resources/documents-resources";
import { ForumAccess } from "../../resources/messaging-resources";
import {
  ProposalAccess,
  WorkAgreementAccess
} from "../../resources/work-resources";
import AddPermissionDialog from "./add-permission-dialog";
import { addPermissionTemplate, getErrorLoadingPermissionTemplatesByQuery, getIsLoadingPermissionTemplatesByQuery, populatePermissionTemplatesByQuery, removePermissionTemplate, updatePermissionTemplate } from "permissions/entities/permission/store/permission-templates-redux-slice";
import { ConfirmResponse, useConfirmDialog } from "app/providers/confirm-dialog";
import NameCell, { NameCellHandle } from "permissions/entities/permission/view/components/permissions-table/components/name-cell";
import DescriptionCell, { DescriptionCellHandle } from "permissions/entities/permission/view/components/permissions-table/components/description-cell";

export default function PermissionsTable(props: Readonly<PermissionProps>) {
  // Refs
  const tableCellRef = useRef<HTMLTableCellElement>(null);
  const nameCellRef = useRef<NameCellHandle | null>(null);
  const descriptionCellRef = useRef<DescriptionCellHandle | null>(null);

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const theme = useTheme();
  const confirm = useConfirmDialog();
  const {
    columnFilters, setColumnFilters,
    sorting, setSorting,
    pagination, setPagination,
  } = usePermissionsQuerySync();

  // Redux dispatch & dialogs
  const dispatch = useAttorneyHubDispatch();
  const { openDialog } = useDialog();

  // Responsive queries
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Category
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setActiveCategory((params.get("category") as CategoryLabel) || '');
  }, [location.search]);

  // State
  const [creatingRow, setCreatingRow] = useState<boolean>(false);
  const [editingRow, setEditingRow] = useState<Row<TableRow> | null>(null);
  const [templateName, setTemplateName] = useState<string>('');
  const [templateDescription, setTemplateDescription] = useState<string>('');
  const [templateResources, setTemplateResources] = useState<Resource[]>([]);
  const [nameIsValid, setNameIsValid] = useState(false);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const hasParent = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("hasParent") === "true";
  }, [location.search]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);
  const [activeCategory, setActiveCategory] = useState<CategoryLabel | ''>(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("category") as CategoryLabel) || '';
  });
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<string>("");
  const [toastSeverity, setToastSeverity] = useState<AlertProps["severity"]>("error");

  const isSingleGranteeView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.has("granteeUserId");
  }, [location.search]);

  const isManageTemplatesView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("action") === 'manage-templates';
  }, [location.search]);

  const hasAdditionalFilters: boolean =
    new URLSearchParams(location.search).has("categories") ||
    new URLSearchParams(location.search).has("granteeUserIds") ||
    new URLSearchParams(location.search).has("accessType") ||
    new URLSearchParams(location.search).has("documentIds") ||
    new URLSearchParams(location.search).has("proposalIds") ||
    new URLSearchParams(location.search).has("workAgreementIds");

  const topLevelTemplates: TopLevelTemplatesIndex = useMemo(() => {
    const byConstructor = new Map<ResourceConstructor, Resource>();
    const legalByAccessType = new Map<string, Resource>();
    const docContentsBySubject = new Map<string, Resource>();

    for (const resource of Permission.topLevelResources()) {
      const Constructor = resource.constructor as ResourceConstructor;
      if (!byConstructor.has(Constructor)) byConstructor.set(Constructor, resource);

      if (resource instanceof LegalEntityAccess) {
        const key = enumKeyOf(LegalEntityAccessType, resource.accessType);
        if (!legalByAccessType.has(key)) legalByAccessType.set(key, resource);
      }

      if (resource instanceof DocumentContentsAccess) {
        const key = enumKeyOf(DocumentSubjectMatter, resource.subjectMatter);
        if (!docContentsBySubject.has(key)) docContentsBySubject.set(key, resource);
      }
    }

    return {
      byConstructor,
      legalByAccessType,
      docContentsBySubject
    };
  }, []);

  const topLevelResources = useMemo(
    () =>
      Array.from(topLevelTemplates.byConstructor.keys()).sort((a, b) =>
        classNameFromResource(a).localeCompare(classNameFromResource(b), undefined, { sensitivity: 'base' }),
      ),
    [topLevelTemplates.byConstructor],
  );

  // Permissions query
  const interfaceName = "permissionsTable";
  const permissionsQuery = useSelector(
    (state: RootState) =>
      state.permissions.interfaceQueries.entries[interfaceName]
  );
  const permissionsResponse = useSelector(
    (state: RootState) =>
      permissionsQuery ? state.permissions.byGranteeId.entries[permissionsQuery.cacheKey()] : undefined
  );
  const isLoadingPermissions = getIsLoadingPermissionsByGranteeId(permissionsQuery);
  const permissionsLoadErrors = getErrorLoadingPermissionsByGranteeId(permissionsQuery);

  // Permission Templates query
  const templatesInterfaceName = "permissionTemplatesTable";
  const permissionTemplatesQuery = useSelector(
    (state: RootState) =>
      state.permissionTemplates.interfaceQueries.entries[templatesInterfaceName]
  );
  const permissionTemplatesResponse = useSelector(
    (state: RootState) =>
      permissionTemplatesQuery ? state.permissionTemplates.byQuery.entries[permissionTemplatesQuery.cacheKey()] : undefined
  );
  const isLoadingPermissionTemplates = getIsLoadingPermissionTemplatesByQuery(permissionTemplatesQuery);
  const permissionTemplatesLoadErrors = getErrorLoadingPermissionTemplatesByQuery(permissionTemplatesQuery);

  const {
    isRefetching,
    userIds,
    documentIds,
    forumIds,
    proposalIds,
    workAgreementIds,
    entityAgreementIds,
  } = usePopulateRelatedEntities(permissionsResponse);

  // Load related entities
  const individuals: Individual[] = getIndividualsByUserIds(userIds);
  const loadingIndividualUserIds: Guid[] = getLoadingIndividualUserIds();
  const areErrorsLoadingIndividuals: boolean = getAreErrorsLoadingIndividualsByUserIds(userIds);
  const allIndividualErrors: IndividualError[] = getAllIndividualErrors();

  const documents: Array<Document | UnavailableDocument> = getDocumentsByIds(documentIds);
  const loadingDocumentIds: Guid[] = getLoadingDocumentIds();
  const areErrorsLoadingDocuments: boolean = getAreErrorsLoadingDocumentsByIds(documentIds);
  const allDocumentErrors: DocumentError[] = getAllDocumentErrors();

  const forums: Forum[] = getForumsByIds(forumIds);
  const loadingForumIds: Guid[] = getLoadingForumIds();
  const areErrorsLoadingForums: boolean = getAreErrorsLoadingForumsByIds(forumIds);
  const allForumErrors: ForumError[] = getAllForumErrors();

  const barebonesProposals: BarebonesProposalInfoAPIResponse[] = getProposalsByIds(proposalIds);
  const loadingProposalIds: Guid[] = getLoadingProposalIds();
  const areErrorsLoadingProposals: boolean = getAreErrorsLoadingProposalsByIds(proposalIds);
  const allProposalErrors: ProposalError[] = getAllProposalErrors();

  const barebonesWorkAgreements: BarebonesWorkAgreementInfoAPIResponse[] = getWorkAgreementsByIds(workAgreementIds);
  const loadingWorkAgreementIds: Guid[] = getLoadingWorkAgreementIds();
  const areErrorsLoadingWorkAgreements: boolean = getAreErrorsLoadingWorkAgreementsByIds(workAgreementIds);
  const allWorkAgreementErrors: WorkAgreementError[] = getAllWorkAgreementErrors();

  const entityAgreements: EntityAgreement[] = getEntityAgreementsByIds(entityAgreementIds);
  const loadingEntityAgreementIds: Guid[] = getLoadingEntityAgreementIds();
  const areErrorsLoadingEntityAgreements: boolean = getAreErrorsLoadingEntityAgreementsByIds(entityAgreementIds);
  const allEntityAgreementErrors: EntityAgreementError[] = getAllEntityAgreementErrors();

  const legalEntities: UserEntityInfo[] = session.entities ?? [];

  const individualByUserId = useMemo(() => {
    const map = new Map<string, Individual>();
    for (const i of individuals) map.set(i.userId.toString(), i);
    return map;
  }, [individuals]);

  const documentById = useMemo(() => {
    const map = new Map<string, Document | UnavailableDocument>();
    for (const d of documents) map.set(d.id.toString(), d);
    return map;
  }, [documents]);

  const proposalById = useMemo(() => {
    const map = new Map<string, BarebonesProposalInfoAPIResponse>();
    for (const p of barebonesProposals) {
      if (!p.id) continue;
      map.set(p.id.toString(), p);
    }
    return map;
  }, [barebonesProposals]);

  const workAgreementById = useMemo(() => {
    const map = new Map<string, BarebonesWorkAgreementInfoAPIResponse>();
    for (const w of barebonesWorkAgreements) {
      if (!w.id) continue;
      map.set(w.id.toString(), w);
    }
    return map;
  }, [barebonesWorkAgreements]);

  const loadingUserIdsSet = useMemo(() => (
    new Set(loadingIndividualUserIds.map(u => u.toString()))
  ), [loadingIndividualUserIds]);

  const individualErrorByUserId = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const e of allIndividualErrors) m.set(e.userId.toString(), e.error?.message || null);
    return m;
  }, [allIndividualErrors]);

  // Autocomplete filter options
  const [entityMembers, setEntityMembers] = useState<EntityMember[]>([]);
  const granteeOptions = useMemo(() =>
    entityMembers.map((member) => ({
      value: member.userId.toString(),
      label: member.name.toString(),
    })),
    [entityMembers]
  );
  const granteeAutocompleteProps = useAutocompleteFilterProps(
    "grantee",
    granteeOptions,
    "Grantee"
  );
  // Load entity members for autocomplete
  useEffect(() => {
    let abortController = new AbortController();
    const entityMemberService = new EntityMemberAPIService(session);
    entityMemberService.getLegalEntityMembersUserInfo(
      new LegalEntity(session.entities?.[0].entityId),
      abortController
    ).then((info) => {
      setEntityMembers(info)
    }).catch((error) => {
      if (error instanceof CanceledError) return;
      console.error(error);
    });

    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, []);

  // Fire a fetch whenever the query changes
  useEffect(() => {
    const permissionPromise = dispatch(populatePermissionsByGranteeId({
      session,
      query: permissionsQuery
    }));

    return () => {
      permissionPromise.abort();
    }
  }, [permissionsQuery]);

  // Fire a fetch whenever the query changes
  useEffect(() => {
    const templatePromise = dispatch(populatePermissionTemplatesByQuery({
      session,
      query: permissionTemplatesQuery
    }));

    return () => {
      templatePromise.abort();
    }
  }, [permissionTemplatesQuery]);

  useEffect(() => {
    if (permissionsLoadErrors) {
      showToast(permissionsLoadErrors, 'error');
    }
    if (permissionTemplatesLoadErrors) {
      showToast(permissionTemplatesLoadErrors, 'error');
    }
  }, [permissionsLoadErrors, permissionTemplatesLoadErrors]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if ((creatingRow || isManageTemplatesView) && params.has("granteeUserId")) {
      params.delete("granteeUserId");
      navigate({ search: params.toString() });
    }
  }, [location.search]);

  const showToast = (message: string, severity: AlertProps["severity"]) => {
    setToastMsg(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const openAddPermissionDialog = useCallback(() => {
    openDialog({
      resetStack: true,
      component: <AddPermissionDialog />,
      title: "Add Permission",
      MuiProps: { maxWidth: "md" }
    });
  }, [openDialog]);

  const handleTogglePermission = useCallback((permission?: Permission) => {
    if (!permission) return console.warn("handleTogglePermission: Permission not found");
    if (permission.isVirtual) {
      dispatch(addPermission({ session, permission }))
        .unwrap()
        .catch(error => showToast(error, 'error'));
      return;
    }

    const isDisabled = Boolean(permission.disabledDate);
    const action = isDisabled
      ? enablePermission({ session, permission })
      : disablePermission({ session, permission });

    dispatch(action).unwrap().catch(error => showToast(error, 'error'));
  }, [dispatch, session]);

  const handleToggleTemplateResource = async (resource?: Resource) => {
    if (!resource) {
      console.warn("handleToggleTemplateResource: Resource not found");
      return;
    }

    if (creatingRow || (editingRow && isTemplateRow(editingRow?.original ?? null))) {
      const existing = templateResources.find(r => r.isEqualTo(resource));
      if (existing) {
        const updated = await existing.clone();
        updated.isDisabled = !updated.isDisabled;
        setTemplateResources(prev => prev.map(r => r.isEqualTo(existing) ? updated : r));
      } else {
        const fresh = await resource.clone();
        fresh.isDisabled = !fresh.isDisabled;
        setTemplateResources(prev => prev.map(r => r.isEqualTo(fresh) ? fresh : r));
      }
    }
  };

  const handleChangeScope = (permission: Permission, newScopeKey: number | undefined) => {
    if (!permission.resource || newScopeKey === undefined) return;
    const newScope: string = Scope[newScopeKey];
    if (newScope === undefined) return;

    const updated = permission.clone();
    if ('scope' in updated.resource) updated.resource.scope = newScope;
    dispatch(addPermission({ session, permission: updated }))
      .unwrap()
      .catch(error => showToast(error, 'error'));
  };

  const handleChangeAccessLevel = (permission: Permission, newAccessLevelKey: number | undefined) => {
    if (!permission.resource || newAccessLevelKey === undefined) return;
    const newAccessLevel = AccessLevel[newAccessLevelKey];
    if (newAccessLevel === undefined) return;

    const updated = permission.clone();
    if ('accessLevel' in updated.resource) updated.resource.accessLevel = newAccessLevel;
    dispatch(addPermission({ session, permission: updated }))
      .unwrap()
      .catch(error => showToast(error, 'error'));
  };

  const handleChangeAccessType = (permission: Permission, newAccessTypeKey: number | undefined) => {
    if (!permission.resource || newAccessTypeKey === undefined) return;
    const newAccessType: string = AccessTypeEnumByResourceType[
      permission.resource.resourceType as keyof typeof AccessTypeEnumByResourceType
    ]?.[newAccessTypeKey];
    if (newAccessType === undefined) return;

    const updated = permission.clone();
    if ('accessType' in updated.resource) updated.resource.accessType = newAccessType;
    dispatch(addPermission({ session, permission: updated }))
      .unwrap()
      .catch(error => showToast(error, 'error'));
  };

  function handleClearFiltersClicked() {
    navigate({ search: clearPermissionFilters(location.search) });
  }

  const applyAccessControlChange = (
    effective: Permission,
    resource: Resource,
    meta: ControlMeta,
    newValue: string
  ) => {
    if (meta.type === MetaControlType.Scope && "scope" in resource) {
      const updatedValue = enumParse(Scope, newValue) as number | undefined;
      handleChangeScope(effective, updatedValue);
      return;
    }
    if (meta.type === MetaControlType.AccessLevel && "accessLevel" in resource) {
      const updatedValue = enumParse(AccessLevel, newValue) as number | undefined;
      handleChangeAccessLevel(effective, updatedValue);
    }
    if (meta.type === MetaControlType.AccessType && "accessType" in resource) {
      const en = AccessTypeEnumByResourceType[resource.resourceType as keyof typeof AccessTypeEnumByResourceType];
      const updatedValue = enumParse(en, newValue) as number | undefined;
      handleChangeAccessType(effective, updatedValue);
    }
  };

  const handleTemplateAccessControlChange = async (
    resource: Resource,
    meta: ControlMeta,
    newValue: string
  ) => {
    let updatedResource: Resource = await resource.clone();

    if (meta.type === MetaControlType.Scope && "scope" in resource) {
      const scopeKey = enumParse(Scope, newValue) as number | undefined;
      if (scopeKey === undefined) {
        console.warn("handleTemplateAccessControlChange: Unable to parse scope");
        return;
      }
      const newScope = Scope[scopeKey];
      if ('scope' in updatedResource) updatedResource.scope = newScope;
    }

    if (meta.type === MetaControlType.AccessLevel && "accessLevel" in resource) {
      const accessLevelKey = enumParse(AccessLevel, newValue) as number | undefined;
      if (accessLevelKey === undefined) {
        console.warn("handleTemplateAccessControlChange: Unable to parse access level");
        return;
      }
      const newAccessLevel = AccessLevel[accessLevelKey];
      if ('accessLevel' in updatedResource) updatedResource.accessLevel = newAccessLevel;
    }

    if (meta.type === MetaControlType.AccessType && "accessType" in resource) {
      const en = AccessTypeEnumByResourceType[resource.resourceType as keyof typeof AccessTypeEnumByResourceType];
      const accessTypeKey = enumParse(en, newValue) as number | undefined;
      if (accessTypeKey === undefined) {
        console.warn("handleTemplateAccessControlChange: Unable to parse access type");
        return;
      }
      const newAccessType = en[accessTypeKey];
      if ('accessType' in updatedResource) updatedResource.accessType = newAccessType;
    }

    setTemplateResources(prev => prev.map(r => r.isEqualTo(resource) ? updatedResource : r));
  };

  const renderTopToolbarCustomActionsCb = useCallback(
    (tableProps: { table: TableInstance<TableRow> }) => {
      const filterStateWithoutGrantee = (filters: ColumnFiltersState) =>
        (filters ?? []).filter(f => f.id !== "grantee");
      const filters: JSX.Element | undefined = (!isSingleGranteeView && !creatingRow && !isManageTemplatesView) ? (
        <Autocomplete
          multiple={false}
          options={granteeOptions}
          getOptionLabel={(o) => o?.label ?? ''}
          isOptionEqualToValue={(o, v) => o?.value === v?.value}
          value={(() => {
            const f = (columnFilters ?? []).find(f => f.id === 'grantee');
            return f ? granteeOptions.find(o => o.value === f.value) ?? null : null;
          })()}
          onChange={(_, newValue) => {
            setColumnFilters((prev) => {
              const without = filterStateWithoutGrantee(prev);
              if (!newValue) {
                const params = new URLSearchParams(location.search);
                params.delete("granteeUserIds");
                navigate({ search: params.toString() });

                return without;
              }

              const params = new URLSearchParams(location.search);
              params.set("granteeUserIds", newValue.value);
              navigate({ search: params.toString() });

              return [
                ...without,
                { id: "grantee", value: newValue.value, type: "equals" as const },
              ];
            });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Grantee" size="small" />
          )}
          sx={{
            minWidth: 200,
            maxWidth: 340,
            mb: 1,
            mt: 1.5
          }}
        />
      ) : undefined;
      const additionalActions: JSX.Element = (
        <PermissionsToolbar
          table={tableProps.table}
          search={location.search}
          navigate={navigate}
          isSingleGranteeView={isSingleGranteeView}
          isCreateTemplateView={!!creatingRow}
          isManageTemplatesView={isManageTemplatesView}
          isFilterPanelOpen={isFilterPanelOpen}
          onToggleFilterPanel={() => setIsFilterPanelOpen(v => !v)}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          hasParent={hasParent}
          individualByUserId={individualByUserId}
          documentById={documentById}
          proposalById={proposalById}
          workAgreementById={workAgreementById}
          onBackButtonClicked={resetState}
          onClearFiltersClicked={handleClearFiltersClicked}
        />
      );

      return props.renderTopToolbarCustomActions(tableProps, additionalActions, filters);
    },
    [
      location.search,
      navigate,
      isSingleGranteeView,
      creatingRow,
      isManageTemplatesView,
      isFilterPanelOpen,
      activeCategory,
      individualByUserId,
      documentById,
      proposalById,
      workAgreementById,
      props.renderTopToolbarCustomActions,
      granteeAutocompleteProps
    ]
  );

  // Child constructors and child types present
  const childAccessTypes = useMemo(
    () => childResourceConstructors(), []
  );

  // Which child resource types exist in the current page of data
  const childTypesInData: ResourceConstructor[] = useMemo<ResourceConstructor[]>(
    (): ResourceConstructor[] => {
      const resourceSet = new Set<ResourceConstructor>();

      for (const row of permissionsResponse?.data ?? []) {
        for (const permission of row.recentPermissionsPreview ?? []) {
          if (!isTopLevelParent(permission)) {
            resourceSet.add(permission.resource.constructor as ResourceConstructor);
          }
        }
      }

      for (const accessType of childAccessTypes) resourceSet.add(accessType);

      return Array.from(resourceSet).sort((a, b) =>
        classNameFromResource(a).localeCompare(
          classNameFromResource(b),
          undefined,
          { sensitivity: 'base' }
        )
      );
    },
    [permissionsResponse?.data, childAccessTypes]
  );

  function getCardSubheader(resource: Resource): string | undefined {
    if (resource instanceof DocumentAccess) {
      return getDocumentName(
        resource, loadingDocumentIds, allDocumentErrors, documents
      );
    }
    if (resource instanceof ForumAccess) {
      return getForumName(
        resource, loadingForumIds, allForumErrors, forums
      );
    }
    if (resource instanceof EntityAgreementAccess) {
      return getEntityAgreementName(
        resource, loadingEntityAgreementIds, allEntityAgreementErrors, entityAgreements
      );
    }
    if (resource instanceof ProposalAccess) {
      return getProposalName(
        resource, loadingProposalIds, allProposalErrors, barebonesProposals
      );
    }
    if (resource instanceof WorkAgreementAccess) {
      return getWorkAgreementName(
        resource, loadingWorkAgreementIds, allWorkAgreementErrors, barebonesWorkAgreements
      );
    }
    if (resource instanceof LegalEntityAccess) {
      return getLegalEntityName(session, resource, legalEntities);
    }
    return undefined;
  }

  function getCandidates(row: TableRow, ctor: ResourceConstructor) {
    if (isPermissionsRow(row)) {
      return row.recentPermissionsPreview.filter((permission) => 
        permission.resource instanceof ctor && 
      (hasParent ? !isTopLevelParent(permission) : isTopLevelParent(permission))
      );
    }

    if (isTemplateRow(row)) {
      let source: Resource[];
      const isCreatingThisRow = creatingRow && row.id?.isEqualTo?.(Guid.empty);
      const isEditingThisRow =
        Boolean(editingRow?.original) && editingRow!.original.id?.isEqualTo?.(row.id);

      if (isEditingThisRow || isCreatingThisRow) {
        source = templateResources;
      } else {
        source = row.resources;
      }
      return source.filter((res) => res instanceof ctor) ?? [];
    }

    return [];
  }

  function renderSingleTopLevelPermission(
    row: TableRow | null,
    resource: ResourceConstructor,
    canEdit: boolean
  ): JSX.Element {
    const config: UniversalCellConfig = {
      cardType: CardType.Single,
      resource,
      candidates: getCandidates,
      template: (ctor) => topLevelTemplates.byConstructor.get(ctor),
      title: ({ resource, permission }) => {
        if (isSingleGranteeView || creatingRow || isManageTemplatesView) {
          return classNameFromResource(resource);
        }
        return permission.isDisabled ? 'Disabled' : 'Enabled';
      },
      colSpan: 1,
      rowSpan: 1,
      canEdit
    };
    return (
      <PermissionCard
        key={resource.name}
        row={row}
        config={config}
        isSingleGranteeView={isSingleGranteeView}
        isCreateTemplateView={!!creatingRow}
        isManageTemplatesView={isManageTemplatesView}
        theme={theme}
        activeCategory={activeCategory}
        individuals={individuals}
        loadingIndividualUserIds={loadingIndividualUserIds}
        getCardSubheader={getCardSubheader}
        onTogglePermission={handleTogglePermission}
        onToggleTemplate={handleToggleTemplateResource}
        onApplyPermissionMeta={applyAccessControlChange}
        onApplyTemplateMeta={handleTemplateAccessControlChange}
      />
    );
  }

  function renderGroupedPermission(
    row: TableRow,
    config: GroupedCellConfig
  ): JSX.Element {
    return (
      <PermissionCard
        row={row}
        config={config}
        isSingleGranteeView={isSingleGranteeView}
        isCreateTemplateView={!!creatingRow}
        isManageTemplatesView={isManageTemplatesView}
        theme={theme}
        activeCategory={activeCategory}
        individuals={individuals}
        loadingIndividualUserIds={loadingIndividualUserIds}
        getCardSubheader={getCardSubheader}
        onTogglePermission={handleTogglePermission}
        onToggleTemplate={handleToggleTemplateResource}
        onApplyPermissionMeta={applyAccessControlChange}
        onApplyTemplateMeta={handleTemplateAccessControlChange}
      />
    );
  }

  function renderSinglePermission(
    row: TableRow | null,
    resource: ResourceConstructor,
    canEdit: boolean
  ): JSX.Element | null {
    if (!row) return null;
    const config: UniversalCellConfig = {
      cardType: CardType.Single,
      resource,
      candidates: getCandidates,
      title: ({ resource, permission }) => {
        if (isSingleGranteeView || creatingRow || isManageTemplatesView) {
          return classNameFromResource(resource);
        }
        return permission.isDisabled ? 'Disabled' : 'Enabled';
      },
      colSpan: 1,
      rowSpan: 1,
      canEdit
    };
    return (
      <PermissionCard
        row={row}
        config={config}
        isSingleGranteeView={isSingleGranteeView}
        isCreateTemplateView={!!creatingRow}
        isManageTemplatesView={isManageTemplatesView}
        theme={theme}
        activeCategory={activeCategory}
        individuals={individuals}
        loadingIndividualUserIds={loadingIndividualUserIds}
        getCardSubheader={getCardSubheader}
        onTogglePermission={handleTogglePermission}
        onToggleTemplate={handleToggleTemplateResource}
        onApplyPermissionMeta={applyAccessControlChange}
        onApplyTemplateMeta={handleTemplateAccessControlChange}
      />
    );
  }

  const renderGranteeColumn = useCallback((
    granteeUserId?: Guid
  ) => {
    if (!granteeUserId || granteeUserId.isEqualTo?.(Guid.empty)) {
      return (
        <GranteeNameCell
          userId={Guid.empty}
          name="(Unknown)"
          isLoading={false}
          error={null}
        />
      );
    }
    const key = granteeUserId.value;
    const granteeName = individualByUserId.get(key)?.getFullName();
    const isLoading = loadingUserIdsSet.has(key);
    const error = individualErrorByUserId.get(key) ?? null;

    if (isSingleGranteeView) {
      return (
        <Box>
          <Typography variant="h6" sx={{ pb: 1.5 }}>
            {isLoading ? "Loading..." : (granteeName ?? "(Unknown)")}
          </Typography>
          <ApplyTemplateMenuButton
            disabled={granteeUserId.isEqualTo(session.user?.id)}
            onApply={(template) => {
              table.resetRowSelection();
              applyTemplateToGrantees(template, [granteeUserId]);
            }}
          />
        </Box>
      )
    }

    return (
      <GranteeNameCell
        userId={granteeUserId}
        name={granteeName}
        isLoading={isLoading}
        error={error}
      />
    )
  }, [
    individualByUserId,
    loadingUserIdsSet,
    individualErrorByUserId,
    isSingleGranteeView
  ]);

  const columnHelper = createMRTColumnHelper<TableRow>();

  const NameCellRenderer = useCallback(
    ({ cell, row }: { cell: Cell<TableRow>, row: Row<TableRow> }) => {
      const isCreatingThisRow =
        creatingRow && row.original.id.isEqualTo(Guid.empty);
      const isEditingThisRow = isCreatingThisRow || editingRow?.id === row.id;

      return (
        <NameCell
          key={`${row.id}-${isEditingThisRow ? "edit" : "view"}`}
          ref={isEditingThisRow ? nameCellRef : null}
          label={String(cell.column.columnDef.header ?? 'Name')}
          name={(row.original as PermissionTemplate).name}
          isReadOnly={!isEditingThisRow && !row.original.id?.isEqualTo(Guid.empty)}
          onValidityChange={isEditingThisRow ? setNameIsValid : undefined}
        />
      );
    },
    [
      creatingRow,
      editingRow?.id,
    ]
  );

  const DescriptionCellRenderer = useCallback(
    ({ cell, row }: { cell: Cell<TableRow>, row: Row<TableRow> }) => {
      const isCreatingThisRow =
        creatingRow && row.original.id.isEqualTo(Guid.empty);
      const isEditingThisRow = isCreatingThisRow || editingRow?.id === row.id;
      const description = (row.original as PermissionTemplate).description;
      if (!isEditingThisRow && !description) {
        return (
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              fontStyle: 'italic'
            }}>
            No description
          </Typography>
        )
      }
      return (
        <DescriptionCell
          key={`${row.id}-${isEditingThisRow ? "edit" : "view"}`}
          ref={isEditingThisRow ? descriptionCellRef : null}
          label={String(cell.column.columnDef.header ?? 'Description')}
          description={description}
          isReadOnly={!isEditingThisRow && !row.original.id.isEqualTo(Guid.empty)}
        />
      );
    },
    [
      creatingRow,
      editingRow?.id
    ]
  );

  const CreatedDateCellRenderer = useCallback(
    ({ row }: { row: Row<TableRow> }) => {
      const createdDate = (row.original as PermissionTemplate).createdDate;
      return (
        <Typography variant="body1" sx={{ alignContent: 'center', textAlign: 'left' }}>
          {createdDate?.format('MM/DD/YYYY') ?? <Icon>remove</Icon>}
        </Typography>
      );
    }, []
  );

  const PermissionsCellRenderer = useCallback(
    ({ row }: { row: Row<TableRow> }) => {
      if (creatingRow && !row.original.id.isEqualTo(Guid.empty)) return;
      return (
        <SingleGranteeResourceCell
          row={row.original}
          topLevelResources={topLevelResources}
          topLevelTemplates={topLevelTemplates}
          isSingleGranteeView={isSingleGranteeView}
          isManageTemplatesView={isManageTemplatesView}
          canEdit={
            row?.id === editingRow?.id
            || (creatingRow && row.original.id.isEqualTo(Guid.empty))
          }
          renderLegalEntityCell={(row, topLevelTemplates, isSingleGranteeView, canEdit) => (
            <LegalEntityGroupCell
              key='legal-entity-group-cell'
              row={row}
              topLevelTemplates={topLevelTemplates}
              isSingleGranteeView={isSingleGranteeView}
              canEdit={canEdit}
              creatingRow={creatingRow}
              editingRow={editingRow}
              resources={templateResources}
              isCreateTemplateView={!!creatingRow}
              isManageTemplatesView={isManageTemplatesView}
              renderGroupedPermission={renderGroupedPermission}
            />
          )}
          renderDocumentSubjectMatterCell={(row, topLevelTemplates, isSingleGranteeView, canEdit) => (
            <DocumentSubjectMatterGroupCell
              key='document-subject-matter-group-cell'
              row={row}
              topLevelTemplates={topLevelTemplates}
              isSingleGranteeView={isSingleGranteeView}
              isCreateTemplateView={!!creatingRow}
              isManageTemplatesView={isManageTemplatesView}
              canEdit={canEdit}
              creatingRow={creatingRow}
              editingRow={editingRow}
              resources={templateResources}
              renderGroupedPermission={renderGroupedPermission}
            />
          )}
          renderSingleTopLevelPermission={renderSingleTopLevelPermission}
        />
      );
    },
    [
      creatingRow,
      topLevelResources,
      topLevelTemplates,
      isSingleGranteeView,
      isManageTemplatesView,
      editingRow,
      renderSingleTopLevelPermission
    ]
  );

  const columns = useMemo(() => {
    const columnsToShow = [];

    if (!creatingRow && !isManageTemplatesView) {
      const granteeCell = {
        id: "grantee",
        header: "Grantee",
        minSize: 250,
        muiTableHeadCellProps: { align: 'left' },
        muiTableBodyCellProps: {
          sx: {
            alignItems: 'start',
            borderBottom: isSingleGranteeView ? 0 : undefined,
            borderTop: isSingleGranteeView ? 0 : undefined,
            justifyContent: 'center'
          }
        },
        enableColumnFilter: false,
        grow: 0,
        Cell: ({ row }: { row: Row<TableRow> }) => (
          isPermissionsRow(row.original)
            ? renderGranteeColumn(row.original.granteeUserId)
            : null
        )
      } satisfies ColumnDef<TableRow, string>;
      columnsToShow.push(granteeCell);
    }

    if (isManageTemplatesView) {
      const manageTemplateColumns = [
        columnHelper.display({
          id: 'name',
          header: 'Name',
          grow: 0,
          muiTableBodyCellProps: {
            sx: { textAlign: 'left' },
            tabIndex: (creatingRow || editingRow) ? -1 : undefined
          },
          Cell: NameCellRenderer
        }),
        columnHelper.display({
          id: 'description',
          header: 'Description',
          grow: 0,
          muiTableBodyCellProps: {
            sx: { textAlign: 'left' },
            tabIndex: (creatingRow || editingRow) ? -1 : undefined
          },
          Cell: DescriptionCellRenderer
        })
      ];
      if (!creatingRow) {
        columnsToShow.push(
          columnHelper.display({
            id: 'createdDate',
            header: 'Created Date',
            grow: 0,
            enableEditing: false,
            Cell: CreatedDateCellRenderer
          })
        );
      }
      columnsToShow.push(...manageTemplateColumns);
    }

    if (isSingleGranteeView || creatingRow) {
      columnsToShow.push(
        columnHelper.display({
          id: 'permissions',
          header: 'Permissions',
          visibleInShowHideMenu: false,
          enableColumnActions: false,
          grow: 1,
          muiTableBodyCellProps: {
            ref: tableCellRef,
            sx: {
              borderBottom: isSingleGranteeView ? 0 : undefined,
              whiteSpace: 'nowrap',
              width: 'max-content',
              minWidth: 0
            }
          },
          Cell: PermissionsCellRenderer
        })
      );
      return columnsToShow;
    }

    if (isManageTemplatesView) return columnsToShow;

    // Build column definitions
    function makeColumn(
      id: string,
      header: string,
      Cell: (row: Row<TableRow> | null) => JSX.Element | null
    ) {
      return columnHelper.display({
        id,
        header,
        enableSorting: false,
        enableColumnFilter: false,
        muiTableHeadCellProps: { align: 'center' },
        Cell: ({ row }) => Cell(row)
      });
    }

    const buildGenericResourceColumn = (
      resource: ResourceConstructor
    ) =>
      makeColumn(
        `rt:${resource.name}`,
        classNameFromResource(resource),
        (row) => renderSinglePermission(
          row?.original ?? null,
          resource,
          row?.id === editingRow?.id
        )
      );

    const buildDocumentSubjectMatterColumn = () =>
      makeColumn(
        'group:DocumentContents',
        'Document Contents',
        (row) => (
          <DocumentSubjectMatterGroupCell
            row={row?.original ?? null}
            topLevelTemplates={topLevelTemplates}
            isSingleGranteeView={isSingleGranteeView}
            isCreateTemplateView={!!creatingRow}
            isManageTemplatesView={isManageTemplatesView}
            canEdit={row?.id === editingRow?.id}
            resources={templateResources}
            creatingRow={creatingRow}
            editingRow={editingRow}
            renderGroupedPermission={renderGroupedPermission}
          />
        )
      );

    const buildLegalEntitiesColumn = () =>
      makeColumn(
        'group:LegalEntities',
        'Legal Entities',
        (row) => (
          <LegalEntityGroupCell
            row={row?.original ?? null}
            topLevelTemplates={topLevelTemplates}
            isSingleGranteeView={isSingleGranteeView}
            isCreateTemplateView={!!creatingRow}
            isManageTemplatesView={isManageTemplatesView}
            canEdit={row?.id === editingRow?.id}
            resources={templateResources}
            creatingRow={creatingRow}
            editingRow={editingRow}
            renderGroupedPermission={renderGroupedPermission}
          />
        )
      );

    const buildProposalColumn = () =>
      makeColumn(
        'group:Proposal',
        'Proposal',
        (row) => (
          <ProposalGroupCell
            row={row?.original as PermissionsByGranteeId | null}
            loadingProposalIds={loadingProposalIds}
            allProposalErrors={allProposalErrors}
            barebonesProposals={barebonesProposals}
            renderGroupedPermission={renderGroupedPermission}
          />
        )
      );

    const buildWorkAgreementColumn = () =>
      makeColumn(
        'group:WorkAgreement',
        'Work Agreement',
        (row) => (
          <WorkAgreementGroupCell
            row={row?.original as PermissionsByGranteeId | null}
            loadingWorkAgreementIds={loadingWorkAgreementIds}
            allWorkAgreementErrors={allWorkAgreementErrors}
            barebonesWorkAgreements={barebonesWorkAgreements}
            renderGroupedPermission={renderGroupedPermission}
          />
        )
      );

    const topLevelColumnDefs: SortedColumnDef[] = (() => {
      if (hasParent || hasAdditionalFilters) {
        // Hide top-level permissions
        return [];
      }

      return topLevelResources.map<SortedColumnDef>((resource: ResourceConstructor) => {
        if (resource === LegalEntityAccess) {
          return {
            sortKey: 'Legal Entities',
            columnDefinition: buildLegalEntitiesColumn()
          };
        }
        if (resource === DocumentContentsAccess) {
          return {
            sortKey: 'Document Contents',
            columnDefinition: buildDocumentSubjectMatterColumn()
          };
        }
        return {
          sortKey: classNameFromResource(resource),
          columnDefinition: buildGenericResourceColumn(resource)
        };
      });
    })();

    const childColumnDefs: SortedColumnDef[] = (() => {
      // Allow child columns when filtered -OR- when viewing children (hasParent)
      if (!hasParent && !hasAdditionalFilters) return [];

      return childTypesInData.map((Ctor): SortedColumnDef => {
        if (Ctor === ProposalAccess) {
          return {
            sortKey: 'Proposal',
            columnDefinition: buildProposalColumn()
          };
        }
        if (Ctor === WorkAgreementAccess) {
          return {
            sortKey: 'Work Agreement',
            columnDefinition: buildWorkAgreementColumn()
          };
        }
        return {
          sortKey: classNameFromResource(Ctor),
          columnDefinition: buildGenericResourceColumn(Ctor),
        };
      });
    })();

    const merged = [...topLevelColumnDefs, ...childColumnDefs]
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { sensitivity: 'base' }));

    for (const { columnDefinition } of merged) {
      columnsToShow.push(columnDefinition);
    }

    return columnsToShow;
  }, [
    granteeOptions,
    renderGranteeColumn,
    topLevelResources,
    hasParent,
    childTypesInData,
    columnHelper,
    isSingleGranteeView,
    creatingRow,
    isManageTemplatesView,
    topLevelTemplates,
    loadingProposalIds,
    allProposalErrors,
    barebonesProposals,
    loadingWorkAgreementIds,
    allWorkAgreementErrors,
    barebonesWorkAgreements
  ]);

  const headersById = useMemo(() => {
    const m = new Map<string, string>();
    for (const col of columns) {
      const id = (col.id ?? String(col.accessorKey ?? '')).trim();
      const headerText = col.header ?? id ?? '';
      if (id) m.set(id, headerText);
    }
    return m;
  }, [columns]);

  useEffect(() => {
    const totals = sumPermissionCounts(permissionsResponse);
    let next: Record<string, boolean> = { grantee: true };
    next = {
      ...next,
      ...computeTopLevelVisibility(
        topLevelResources,
        totals,
        { hasParent, hasAdditionalFilters }
      )
    };
    next = {
      ...next,
      ...computeChildVisibility(
        childTypesInData,
        hasAdditionalFilters || hasParent
      )
    };

    next = clampByCategory(next, headersById, activeCategory);

    if (!isEqualVisibility(next, columnVisibility)) {
      setColumnVisibility(next);
    }
  }, [
    permissionsResponse, topLevelResources, childTypesInData,
    hasParent, hasAdditionalFilters, activeCategory, headersById,
    columnVisibility
  ]);

  const tableData = useMemo(() => {
    if (isManageTemplatesView) {
      const rows = [...permissionTemplatesResponse?.data ?? []];

      if (creatingRow) {
        rows.unshift(
          new PermissionTemplate(
            Guid.empty,
            Guid.generate(),
            Array.from(topLevelTemplates.byConstructor.values()),
            "",
            "",
            new AHDate()
          )
        );
      }

      return rows;
    }

    return [...permissionsResponse?.data ?? []];
  },
    [
      permissionsResponse,
      permissionTemplatesResponse,
      isManageTemplatesView,
      creatingRow
    ]
  );

  async function handleTemplateEdit(row: Row<TableRow>, template: PermissionTemplate) {
    table.setEditingRow(row);
    setEditingRow(row);

    const clonedResources = await Promise.all(
      template.resources.map(async r => await r.clone())
    );

    setTemplateName(template.name);
    setTemplateDescription(template.description ?? '');
    setTemplateResources(clonedResources);
    setNameIsValid((template.name ?? '').trim().length > 0);
    table.setExpanded({ [row.id]: true });
  }

  function handleTemplateSave(resetState: () => void) {
    if (!creatingRow && !editingRow) return;

    const draftedName = nameCellRef.current?.getValue()?.trim();
    const draftedDescription = descriptionCellRef.current?.getValue()?.trim();

    const finalName = draftedName && draftedName.length > 0
      ? draftedName
      : templateName?.trim();
    const finalDescription = draftedDescription && draftedDescription.length > 0
      ? draftedDescription
      : templateDescription?.trim();

    if (creatingRow && (!finalName || finalName.length === 0)) {
      showToast("Template name is required.", 'error');
      return;
    }
    if (!session.user?.id) {
      showToast("Unable to determine current user.", 'error');
      return;
    }

    if (finalName && finalName !== templateName) {
      setTemplateName(finalName);
    }
    if (finalDescription && finalDescription !== templateDescription) {
      setTemplateDescription(finalDescription);
    }

    const newTemplate = new PermissionTemplate(
      editingRow ? editingRow.original.id : Guid.generate(),
      session.user.id,
      templateResources,
      finalName ?? '',
      finalDescription ?? ''
    );

    const action = creatingRow
      ? addPermissionTemplate({ session, template: newTemplate })
      : updatePermissionTemplate({ session, template: newTemplate });

    dispatch(action).unwrap()
      .then(() => {
        dispatch(populatePermissionTemplatesByQuery({ session, query: permissionTemplatesQuery }));
        showToast(creatingRow ? "Template created" : "Template updated", 'success');
      })
      .catch(error => showToast(error, 'error'))
      .finally(resetState);
  }

  function handleTemplateDelete(templateId: Guid, resetState: () => void) {
    dispatch(removePermissionTemplate({ session, templateId })).unwrap()
      .then(() => {
        dispatch(populatePermissionTemplatesByQuery({ session, query: permissionTemplatesQuery }));
        showToast("Template deleted", 'info');
      })
      .catch(error => showToast(error, 'error'))
      .finally(resetState);
  }

  async function applyTemplateToGrantees(
    template: PermissionTemplate,
    userIds: Guid[]
  ) {
    // TODO: Allow editing template before applying
    // TODO: Show diff of permission changes for each user

    const response = await confirm({
      title: "Overwrite Existing Permissions?",
      message:
        "Applying a permissions template will overwrite all existing permissions for the selected user(s). Continue?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const apiService = new PermissionsAPIService(session);
      const entityId = session.entities?.[0].entityId;
      const resources = template.resources;

      const newPermissions = [];
      for (const userId of userIds) {
        for (const resource of resources) {
          newPermissions.push(new Permission(resource, userId, entityId));
        }
      }
      const savedPermissions = await apiService.bulkAddPermissions(newPermissions);
      dispatch(addPermissions({ session, permissions: savedPermissions }));
      showToast("Permissions updated from template", 'success');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        showToast(
          "Unable to save Permission: " + error.response.data,
          'warning'
        );
      } else {
        console.error(error);
        showToast(
          "Unable to save Permission",
          'error'
        );
      }
    }
  }

  const resetState = () => {
    table.setCreatingRow(null);
    setCreatingRow(false);
    table.setEditingRow(null);
    setEditingRow(null);
    setTemplateName('');
    setTemplateDescription('');
    setTemplateResources([]);
    table.resetExpanded();
  };

  const table = useMaterialReactTable<TableRow>({
    columns: columns,
    data: tableData,
    enableRowSelection: (!isManageTemplatesView && !creatingRow && !isSingleGranteeView) ? (row) => (
      isPermissionsRow(row.original) && !row.original.granteeUserId.isEqualTo(session.user?.id)
    ) : undefined,
    enableMultiRowSelection: (!isManageTemplatesView && !creatingRow && !isSingleGranteeView) ? (row) => (
      isPermissionsRow(row.original) && !row.original.granteeUserId.isEqualTo(session.user?.id)
    ) : undefined,
    enableTableHead: !isSingleGranteeView,
    enableRowActions: isManageTemplatesView,
    enableColumnPinning: !isSingleGranteeView,
    enableEditing: true,
    displayColumnDefOptions: {
      'mrt-row-select': {
        grow: 0,
        muiTableHeadCellProps: { align: 'center' },
        muiTableBodyCellProps: { align: 'center' }
      },
      'mrt-row-expand': {
        grow: 0,
        muiTableHeadCellProps: {
          sx: {
            align: 'left',
            display: (creatingRow || !isManageTemplatesView)
              ? 'none' : 'table-cell'
          }
        },
        muiTableBodyCellProps: {
          sx: {
            align: 'left',
            display: (creatingRow || !isManageTemplatesView)
              ? 'none' : 'table-cell'
          }
        }
      },
      "mrt-row-actions": {
        grow: 0,
        muiTableHeadCellProps: {
          sx: {
            '& .Mui-TableHeadCell-Content': {
              justifyContent: 'flex-end'
            },
            minWidth: 110
          }
        },
        muiTableBodyCellProps: {
          sx: {
            gap: 0,
            '& > *': {
              gap: 0,
              height: 'auto',
              width: 'auto'
            },
            minWidth: 110,
            marginRight: 1
          }
        }
      }
    },
    createDisplayMode: 'custom',
    editDisplayMode: 'custom',
    initialState: {
      columnFilters,
      columnPinning: {
        left: isSingleGranteeView ? ["mrt-row-select"] : ["mrt-row-select", "grantee"]
      },
      columnOrder: ["mrt-row-select", "grantee", "name", "description", "createdDate", "permissions", ...columns
        .filter(c => c.id !== 'grantee' && c.id !== 'name' && c.id !== 'description' && c.id !== 'createdDate' && c.id !== 'permissions')
        .map(c => c.id ?? String(c.accessorKey ?? ''))
      ],
      columnVisibility,
      density: 'compact',
    },
    positionActionsColumn: 'last',
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    enableExpandAll: false,
    manualPagination: true,
    manualSorting: true,
    layoutMode: (isSingleGranteeView || creatingRow)
      ? "grid" : "semantic",
    columnFilterDisplayMode: "subheader",
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    muiSelectCheckboxProps: { disableRipple: true },
    muiSelectAllCheckboxProps: { disableRipple: true },
    muiTableContainerProps: ({ table }) => {
      const top = table.refs.topToolbarRef.current?.offsetHeight ?? 0;
      const bottom = table.refs.bottomToolbarRef.current?.offsetHeight ?? 0;
      return {
        sx: {
          height: `calc(100% - ${top}px - ${bottom}px)`,
        },
      };
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: () => {
      if (
        permissionsLoadErrors ||
        permissionTemplatesLoadErrors ||
        areErrorsLoadingDocuments ||
        areErrorsLoadingForums ||
        areErrorsLoadingProposals ||
        areErrorsLoadingWorkAgreements ||
        areErrorsLoadingIndividuals ||
        (session.currentEntity.isOfficer && areErrorsLoadingEntityAgreements)
      ) {
        return {
          color: "error",
          children: "Error loading data",
        } satisfies AlertProps;
      }
      return {} satisfies AlertProps;
    },
    muiTableBodyRowProps: ({ row, isDetailPanel }) => ({
      hover: false,
      sx: {
        height: (() => {
          if (row.original.id?.isEqualTo(Guid.empty) || isDetailPanel) {
            return 'auto';
          }
          if (isManageTemplatesView) return '66px';
          if (isSingleGranteeView) return 'auto';
          return '135px';
        })(),
        '&:hover': { backgroundColor: 'unset' }
      }
    }),
    muiTableBodyCellProps: {
      sx: {
        height: 'inherit',
        textAlign: 'center',
        padding: 0.4,
        '& > *': {
          height: '100%'
        }
      }
    },
    muiDetailPanelProps: {
      sx: {
        whiteSpace: 'nowrap',
        width: 'max-content',
        minWidth: 0
      }
    },
    muiExpandButtonProps: ({ row, table }) => ({
      disabled: !!editingRow || row.original.id?.isEqualTo(Guid.empty),
      onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }), // Only 1 detail panel open at a time
      sx: {
        transform: row.getIsExpanded() ? 'rotate(180deg)' : 'rotate(-90deg)',
        transition: 'transform 0.2s'
      },
    }),
    renderDetailPanel: ({ row }) => {
      if (!isManageTemplatesView) return;
      return (
        <SingleGranteeResourceCell
          row={row.original}
          topLevelResources={topLevelResources}
          topLevelTemplates={topLevelTemplates}
          isSingleGranteeView={isSingleGranteeView}
          isManageTemplatesView={isManageTemplatesView}
          canEdit={editingRow?.id === row.id}
          renderLegalEntityCell={(row, topLevelTemplates, isSingleGranteeView, canEdit) => (
            <LegalEntityGroupCell
              key='legal-entity-group-cell'
              row={row}
              topLevelTemplates={topLevelTemplates}
              isSingleGranteeView={isSingleGranteeView}
              canEdit={!!canEdit}
              resources={templateResources}
              creatingRow={creatingRow}
              editingRow={editingRow}
              isCreateTemplateView={!!creatingRow}
              isManageTemplatesView={isManageTemplatesView}
              renderGroupedPermission={renderGroupedPermission}
            />
          )}
          renderDocumentSubjectMatterCell={(row, topLevelTemplates, isSingleGranteeView, canEdit) => (
            <DocumentSubjectMatterGroupCell
              key='document-subject-matter-group-cell'
              row={row}
              topLevelTemplates={topLevelTemplates}
              isSingleGranteeView={isSingleGranteeView}
              isCreateTemplateView={!!creatingRow}
              isManageTemplatesView={isManageTemplatesView}
              canEdit={!!canEdit}
              resources={templateResources}
              creatingRow={creatingRow}
              editingRow={editingRow}
              renderGroupedPermission={renderGroupedPermission}
            />
          )}
          renderSingleTopLevelPermission={renderSingleTopLevelPermission}
        />
      );
    },
    renderRowActions: ({ row }) => {
      const originalRow = row.original as PermissionTemplate;

      return (
        <ManageTemplateRowActions
          templateId={originalRow.id}
          isCreating={creatingRow}
          editingRow={editingRow}
          isLoading={isLoadingPermissionTemplates}
          canSave={nameIsValid}
          onEdit={() => handleTemplateEdit(row, originalRow)}
          onSave={() => handleTemplateSave(resetState)}
          onCancel={() => {
            setNameIsValid(false);
            resetState();
          }}
          onDelete={(templateId) => handleTemplateDelete(templateId, resetState)}
        />
      );
    },
    renderToolbarAlertBannerContent: ({ table }) => {
      const selectedRows = table.getSelectedRowModel().flatRows.map(r => r.original);
      const count = selectedRows.length;
      if (count === 0) return null;
      return (
        <AlertBarContainer>
          <AlertBarSelectionContainer>
            <Typography>{count} user(s) selected</Typography>
            <Button
              sx={{ ml: 2 }}
              size="small"
              color="primary"
              onClick={() => table.resetRowSelection()}
            >
              Clear Selection
            </Button>
          </AlertBarSelectionContainer>
          <ApplyTemplateMenuButton
            onApply={(template) => {
              table.resetRowSelection();
              applyTemplateToGrantees(
                template,
                Array.from(new Set(
                  (selectedRows as PermissionsByGranteeId[])
                    .map(r => r.granteeUserId.value)
                )).map(id => new Guid(id))
              );
            }}
          />
        </AlertBarContainer>
      );
    },
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            {!isManageTemplatesView && !creatingRow && (
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  navigate('/administration/permissions?action=manage-templates');
                }}
              >
                Manage Templates
              </Button>
            )}
            {!creatingRow && !isManageTemplatesView && (
              <Button
                color="primary"
                startIcon={<AddModeratorIcon />}
                variant="outlined"
                onClick={openAddPermissionDialog}
              >
                Add Permission
              </Button>
            )}
            {(!creatingRow && isManageTemplatesView) && (
              <Button
                color="primary"
                startIcon={<PostAddIcon />}
                variant="outlined"
                onClick={async () => {
                  const initialResources = await Promise.all(
                    Array.from(topLevelTemplates.byConstructor.values())
                      .map(async r => await r.clone())
                  );
                  table.toggleAllRowsExpanded(false);
                  table.setCreatingRow(true);
                  setCreatingRow(true);
                  setTemplateName('');
                  setTemplateDescription('');
                  setTemplateResources(initialResources);
                  setNameIsValid(false);
                }}
              >
                Create Template
              </Button>
            )}
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <>
            <MobileButton
              color="primary">
              <ContentCopyIcon fontSize="large" />
            </MobileButton>
            <MobileButton
              color="primary"
              onClick={openAddPermissionDialog}>
              <AddBoxIcon fontSize="large" />
            </MobileButton>
          </>
        )}
      </>
    ),
    renderTopToolbarCustomActions: renderTopToolbarCustomActionsCb,
    renderEmptyRowsFallback: isManageTemplatesView
      ? () => <NoRowsText>No Permission Templates Found</NoRowsText>
      : undefined,
    rowCount: isManageTemplatesView
      ? (permissionTemplatesResponse?.totalElements ?? 0)
      : (permissionsResponse?.totalElements ?? 0),
    state: {
      editingRow,
      columnFilters,
      showColumnFilters: true,
      columnVisibility,
      globalFilter,
      isLoading: (
        !permissionsResponse?.data.length && isLoadingPermissions
      ) || (
          !permissionTemplatesResponse?.data.length && isLoadingPermissionTemplates
        ),
      pagination,
      showAlertBanner: Boolean(
        permissionsLoadErrors ||
        permissionTemplatesLoadErrors ||
        areErrorsLoadingIndividuals ||
        areErrorsLoadingDocuments ||
        areErrorsLoadingProposals ||
        areErrorsLoadingWorkAgreements ||
        areErrorsLoadingForums ||
        (session.currentEntity.isOfficer && areErrorsLoadingEntityAgreements)
      ),
      showProgressBars: (
        isLoadingPermissions ||
        isLoadingPermissionTemplates ||
        isRefetching ||
        loadingIndividualUserIds.length > 0 ||
        loadingDocumentIds.length > 0 ||
        loadingProposalIds.length > 0 ||
        loadingWorkAgreementIds.length > 0 ||
        loadingForumIds.length > 0 ||
        loadingEntityAgreementIds.length > 0
      ),
      sorting,
      showSkeletons: !permissionsResponse
    },
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <Portal>
        <PermissionsFilterDrawer
          isOpen={isFilterPanelOpen}
          onClose={() => setIsFilterPanelOpen(false)}
          onFilter={() => { }}
        />
      </Portal>
      <Portal>
        <Snackbar
          open={toastOpen}
          autoHideDuration={5000}
          onClose={() => setToastOpen(false)}
        >
          <Alert
            onClose={() => setToastOpen(false)}
            severity={toastSeverity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toastMsg}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
}
